import { FC, useState, useContext } from "react";

import { TrainingDrillFolderRequest } from "@/features/training/model/Training/TrainingDrillFolder";
import { CustomCloseButton } from "@/features/training/ui";
import { CustomSaveButton } from "@/features/training/ui";
import { FormDialogue } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderIcon from "@mui/icons-material/Folder";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	DialogActions,
	IconButton,
	LinearProgress,
	Stack,
	Tooltip,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import {
	useMutateDeleteTrainingDrillFolder,
	useMutateUpdateTrainingDrillFolder,
} from "@/service/TrainingMutations";
import { useFetchDrillFolder } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


interface EditDrillFolderProps {
	handleClose: () => void;
	open: boolean;
	id: number;
}

/* ---------- Dialog shell ---------- */
export const EditDrillFolder: FC<EditDrillFolderProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	return (
		<FormDialogue
			handleClose={props.handleClose}
			open={props.open}
			title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.EditDrillFolder.L43C10.EditDrillFolder", "Edit Drill Folder")}
			translationString={"Training.EditDrillFolder.Title"}
			maxWidth="sm"
		>
			<EditDrillFolderForm {...props} />
		</FormDialogue>
	);
};

/* ---------- Form ---------- */
const schema = Yup.object({
	Name: Yup.string().trim().required("Name is required"),
});

export const EditDrillFolderForm: FC<EditDrillFolderProps> = (props) => {
	const { data: folder, isLoading } = useFetchDrillFolder(props.id);
	const updateTrainingDrillFolder = useMutateUpdateTrainingDrillFolder();
	const deleteTrainingDrillFolder = useMutateDeleteTrainingDrillFolder();
	const [isDeleting, setIsDeleting] = useState(false);

	const handleSave = async (request: TrainingDrillFolderRequest) => {
		try {
			await updateTrainingDrillFolder({ request, folderId: props.id });
			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (confirmation.isConfirmed) {
				await deleteTrainingDrillFolder(props.id);
				AlertRequest.deleteConfirmed("Folder deleted successfully.");
			}
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoading || !folder)
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);

	const drillsCount = folder.Drills?.length ?? 0;
	const eventsCount = folder.Events?.length ?? 0;
	const deleteDisabled = drillsCount > 0 || eventsCount > 0;

	return (
		<Formik
			initialValues={{ Name: folder.Name ?? "" }}
			validationSchema={schema}
			onSubmit={handleSave}
			enableReinitialize
		>
			{({ dirty, isValid, isSubmitting }) => (
				<Form>
					{/* Header row with folder icon + chips */}
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ mb: 2, mt: -0.5 }}
					>
						<FolderIcon color="info" fontSize="small" />
						<Chip
							size="small"
							variant="outlined"
							label={`Drills: ${drillsCount}`}
						/>
						<Chip
							size="small"
							variant="outlined"
							label={`Events: ${eventsCount}`}
						/>
					</Stack>

					<CustomInput
						id="Name"
						type="text"
						label="Name"
						name="Name"
						translationString="Training.EditFolder.Name"
						autoFocus
					/>

					{deleteDisabled && (
						<Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
							You can only delete an empty folder. Remove or move the{" "}
							<strong>{drillsCount}</strong> drill(s) and{" "}
							<strong>{eventsCount}</strong> event(s) first.
						</Alert>
					)}

					<DialogActions
						sx={{
							mt: 2,
							px: 0,
							gap: 1,
							flexWrap: "wrap",
							justifyContent: "flex-end",
						}}
					>
						<Tooltip
							title={
								deleteDisabled
									? "Folder must be empty before it can be deleted"
									: "Delete folder"
							}
						>
							<span>
								<IconButton
									color="error"
									onClick={() => {
										void handleDelete();
									}}
									disabled={deleteDisabled || isSubmitting || isDeleting}
									aria-label="Delete folder"
								>
									{isDeleting ? (
										<CircularProgress size={18} color="inherit" />
									) : (
										<DeleteOutlineIcon />
									)}
								</IconButton>
							</span>
						</Tooltip>

						<Box sx={{ flex: 1 }} />

						<CustomCloseButton
							handleClose={props.handleClose}
							disabled={isSubmitting || isDeleting}
						/>
						<CustomSaveButton
							loading={isSubmitting}
							disabled={!dirty || !isValid || isSubmitting || isDeleting}
						/>
					</DialogActions>
				</Form>
			)}
		</Formik>
	);
};
